import { useState } from "react";
import { Link } from "react-router-dom";
import { findDrinks } from "@/lib/api";
import { ArrowLeft, ArrowRight, RotateCcw, Instagram, Share2, Check, Heart, Printer } from "lucide-react";
import { toast } from "sonner";
import { useSavedMatches } from "@/hooks/useSavedMatches";
import { printCocktail } from "@/lib/printCocktail";

const STEPS = [
  { key: "spirit", label: "Preferred Spirit", options: ["Vodka", "Whisky", "Rum", "Gin", "Tequila", "No preference"] },
  { key: "taste", label: "Taste Profile", options: ["Sweet", "Sour", "Fruity", "Bitter", "Smoky", "Refreshing"] },
  { key: "strength", label: "Strength", options: ["Light", "Medium", "Strong"] },
  { key: "occasion", label: "Occasion", options: ["Date Night", "Party", "Dinner", "Girls Night", "Chill"] },
  { key: "budget", label: "Budget", options: [
    { value: "under-500", label: "Under ₹500" },
    { value: "500-1000", label: "₹500 – ₹1,000" },
    { value: "1000-2000", label: "₹1,000 – ₹2,000" },
    { value: "2000-plus", label: "₹2,000+" },
  ]},
];

export default function DrinkFinder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const current = STEPS[step];
  const progress = ((step + (results ? 1 : 0)) / STEPS.length) * 100;

  const pick = async (val) => {
    const next = { ...answers, [current.key]: val };
    setAnswers(next);
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const data = await findDrinks(next);
        setResults(data);
      } catch (e) {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const reset = () => {
    setStep(0);
    setAnswers({});
    setResults(null);
  };

  if (results) return <Results results={results} answers={answers} onReset={reset} />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="drink-finder">
      <div className="flex items-center justify-between mb-4">
        <p className="eyebrow">Drink Finder</p>
        <p className="text-xs font-semibold text-cream/60">Step {step + 1} of {STEPS.length}</p>
      </div>
      <div className="h-1.5 rounded-full bg-ink/10 overflow-hidden mb-8">
        <div className="h-full bg-pink transition-all duration-500" style={{ width: `${progress}%` }} data-testid="progress-bar" />
      </div>

      <h1 className="font-serif-display text-3xl sm:text-4xl md:text-5xl font-bold text-cream leading-tight">
        {step === 0 && <>Let's find your <span className="italic font-serif-editor text-pink-brand">perfect sip.</span></>}
        {step === 1 && <>What flavour <span className="italic font-serif-editor text-pink-brand">calls to you?</span></>}
        {step === 2 && <>How <span className="italic font-serif-editor text-pink-brand">strong</span> tonight?</>}
        {step === 3 && <>What's the <span className="italic font-serif-editor text-pink-brand">vibe?</span></>}
        {step === 4 && <>Set your <span className="italic font-serif-editor text-pink-brand">budget.</span></>}
      </h1>
      <p className="mt-3 text-cream/60 font-body">{current.label}</p>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 animate-fade-up">
        {current.options.map((opt) => {
          const value = typeof opt === "string" ? opt : opt.value;
          const label = typeof opt === "string" ? opt : opt.label;
          const isSelected = answers[current.key] === value;
          return (
            <button
              key={value}
              onClick={() => pick(value)}
              disabled={loading}
              className={`group card-cream p-5 text-left min-h-[110px] flex flex-col justify-between disabled:opacity-50 transition-colors ${isSelected ? "!border-pink-brand ring-1 ring-pink-brand/40" : ""}`}
              data-testid={`quiz-option-${current.key}-${value.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <span className={`font-serif-display text-xl font-bold ${isSelected ? "text-pink-brand" : "text-cream group-hover:text-pink-brand"} transition-colors`}>{label}</span>
              <span className="text-xs uppercase tracking-widest text-cream/50 mt-2 flex items-center justify-between">{isSelected ? "Selected" : "Choose"} <ArrowRight size={14} /></span>
            </button>
          );
        })}
      </div>

      <div className="mt-10 flex justify-between">
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 disabled:opacity-40 hover:text-cream"
          data-testid="quiz-back"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button onClick={reset} className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 hover:text-pink-brand" data-testid="quiz-restart">
          <RotateCcw size={14} /> Restart
        </button>
      </div>

      {loading && (
        <div className="mt-8 text-center text-cream/70 font-body">Mixing your matches…</div>
      )}
    </div>
  );
}

function Results({ results, answers, onReset }) {
  const top = results[0];
  const { isSaved, toggle } = useSavedMatches();
  const saved = isSaved(top.cocktail.slug);
  const handleSave = () => {
    toggle(top.cocktail.slug);
    toast.success(saved ? "Removed from your saves" : `Saved ${top.cocktail.name} to your bar cart`);
  };
  const share = (platform) => {
    const url = "https://sipalchemy.in";
    const text = `I got matched with ${top.cocktail.name} on Sip Alchemy — a ${top.match_score}% match. Take the quiz:`;
    if (platform === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, "_blank");
    } else if (platform === "instagram") {
      navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Copied — paste it in your Instagram Story or DM!");
      window.open("https://instagram.com/", "_blank");
    } else if (platform === "copy") {
      navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied!");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="drink-finder-results">
      <p className="eyebrow">Your Matches</p>
      <h1 className="mt-2 font-serif-display text-3xl sm:text-4xl md:text-5xl font-bold text-cream leading-tight">
        Meet <span className="italic font-serif-editor text-pink-brand">{top.cocktail.name}.</span>
      </h1>
      <p className="mt-3 text-cream/60 font-body">Based on your choices — {Object.values(answers).slice(0, 3).join(" · ")}.</p>

      {/* Top match hero */}
      <div className="mt-8 grid md:grid-cols-2 gap-6 sm:gap-10 items-stretch" data-testid="top-match-card">
        <div className="rounded-2xl overflow-hidden aspect-[4/5] relative">
          <img src={top.cocktail.image} alt={top.cocktail.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
          <div className="absolute top-4 left-4 inline-flex items-center gap-2 h-9 px-4 rounded-full bg-ink text-cream text-xs font-bold uppercase tracking-widest border border-cream/20" data-testid="top-match-score">
            <span className="h-2 w-2 rounded-full bg-pink" /> {top.match_score}% Match
          </div>
        </div>
        <div className="card-cream p-6 sm:p-8 flex flex-col">
          <p className="eyebrow">Top Pick</p>
          <h2 className="mt-2 font-serif-display text-3xl sm:text-4xl font-bold text-cream">{top.cocktail.name}</h2>
          <p className="text-cream/70 mt-3 font-body">{top.cocktail.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="chip chip-ink">{top.cocktail.base_spirit}</span>
            <span className="chip chip-ink">{top.cocktail.strength}</span>
            {top.cocktail.taste_profile.map(t => <span key={t} className="chip chip-pink">{t}</span>)}
          </div>
          <ul className="mt-5 space-y-1.5 text-sm text-cream/80 font-body">
            {top.match_reasons.map((r, i) => (
              <li key={i} className="flex gap-2"><Check size={16} className="text-pink-brand mt-0.5 flex-shrink-0" />{r}</li>
            ))}
          </ul>

          <div className="mt-auto pt-6 flex flex-wrap gap-2">
            <button onClick={handleSave} className={`btn-pill ${saved ? "btn-pink" : "btn-outline-ink"}`} data-testid="save-match-button">
              <Heart size={16} fill={saved ? "currentColor" : "none"} /> {saved ? "Saved" : "Save"}
            </button>
            <button onClick={() => share("whatsapp")} className="btn-pill btn-pink" data-testid="share-whatsapp-button">
              <Share2 size={16} /> WhatsApp
            </button>
            <button onClick={() => share("instagram")} className="btn-pill btn-outline-ink" data-testid="share-instagram-button">
              <Instagram size={16} /> Instagram
            </button>
            <button onClick={() => printCocktail(top.cocktail)} className="btn-pill btn-outline-ink" data-testid="finder-print-recipe">
              <Printer size={16} /> Print
            </button>
          </div>
        </div>
      </div>

      {/* Recipe */}
      <div className="mt-10 card-cream p-6 sm:p-8">
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <p className="eyebrow">Ingredients</p>
            <ul className="mt-3 space-y-1.5 font-body text-cream/85 text-sm sm:text-base">
              {top.cocktail.ingredients.map((i, k) => <li key={k}>· {i}</li>)}
            </ul>
          </div>
          <div>
            <p className="eyebrow">Method</p>
            <ol className="mt-3 space-y-2 font-body text-cream/85 text-sm sm:text-base list-decimal list-inside">
              {top.cocktail.method.map((m, k) => <li key={k}>{m}</li>)}
            </ol>
          </div>
        </div>
        <div className="hr-rule mt-6 mb-6" />
        <p className="font-serif-editor italic text-cream/80"><span className="text-pink-brand font-semibold not-italic uppercase text-xs tracking-widest">Pro Tip · </span>{top.cocktail.pro_tip}</p>
      </div>

      {/* Other matches */}
      <h3 className="mt-14 font-serif-display text-2xl sm:text-3xl font-bold text-cream">You might also love</h3>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {results.slice(1).map((r) => (
          <div key={r.cocktail.id} className="card-cream overflow-hidden" data-testid={`alt-match-${r.cocktail.slug}`}>
            <div className="aspect-[4/3] relative">
              <img src={r.cocktail.image} alt={r.cocktail.name} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 chip chip-pink bg-pink text-cream border-transparent">{r.match_score}% Match</div>
            </div>
            <div className="p-5">
              <p className="font-serif-display text-xl font-bold text-cream">{r.cocktail.name}</p>
              <p className="text-xs text-cream/60 mt-1">{r.cocktail.base_spirit} · {r.cocktail.strength}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-col sm:flex-row gap-3">
        <button onClick={onReset} className="btn-pill btn-ink" data-testid="retake-quiz">
          <RotateCcw size={16} /> Retake Quiz
        </button>
        <Link to="/cocktails" className="btn-pill btn-outline-ink" data-testid="browse-all-cocktails">
          Browse all cocktails <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
}
